import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Building2, Save, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/students');

const studentFormSchema = z.object({
  studentNumber: z.string().min(1, '学籍番号は必須です'),
  nameLast: z.string().min(1, '姓は必須です'),
  nameFirst: z.string().min(1, '名は必須です'),
  nameKanaLast: z.string().optional(),
  nameKanaFirst: z.string().optional(),
  email: z.string().email('有効なメールアドレスを入力してください'),
  structureId: z.string().uuid('所属組織を選択してください'),
  enrollmentYear: z.coerce.number().int().min(2000, '有効な年度を入力してください'),
  status: z.enum(['ENROLLED', 'LEAVE', 'GRADUATED', 'WITHDRAWN']),
});

type StudentFormValues = z.infer<typeof studentFormSchema>;

export default function StudentForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [structures, setStructures] = useState<{ id: string; nameFormal: string; nameShort: string }[]>([]);

  const form = useForm<StudentFormValues>({
    resolver: zodResolver(studentFormSchema),
    defaultValues: {
      studentNumber: '',
      nameLast: '',
      nameFirst: '',
      nameKanaLast: '',
      nameKanaFirst: '',
      email: '',
      structureId: '',
      enrollmentYear: new Date().getFullYear(),
      status: 'ENROLLED',
    },
  });

  const fetchMasterData = useCallback(async () => {
    try {
      const res = await fetch(apiUrl('/api/organizations'));
      if (res.ok) {
        setStructures((await res.json()) as { id: string; nameFormal: string; nameShort: string }[]);
      }
    } catch (error) {
      console.error('Failed to fetch structures', error);
    }
  }, []);

  const fetchStudent = useCallback(
    async (studentId: string) => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/${studentId}`);
        if (res.ok) {
          const data = (await res.json()) as StudentFormValues;
          form.reset({
            studentNumber: data.studentNumber,
            nameLast: data.nameLast,
            nameFirst: data.nameFirst,
            nameKanaLast: data.nameKanaLast || '',
            nameKanaFirst: data.nameKanaFirst || '',
            email: data.email,
            structureId: data.structureId,
            enrollmentYear: data.enrollmentYear,
            status: data.status,
          });
        }
      } catch (error) {
        console.error('Failed to fetch student', error);
      } finally {
        setLoading(false);
      }
    },
    [form],
  );

  useEffect(() => {
    fetchMasterData();
    if (isEdit) {
      fetchStudent(id);
    }
  }, [id, isEdit, fetchMasterData, fetchStudent]);

  const onSubmit = async (values: StudentFormValues) => {
    try {
      setLoading(true);
      const url = isEdit ? `${API_BASE}/${id}` : API_BASE;
      const method = isEdit ? 'PUT' : 'POST'; // Note: PUT not implemented in mock route yet, but standard practice

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (res.ok) {
        navigate('/admin/enrollment/students');
      }
    } catch (error) {
      console.error('Failed to save student', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/enrollment/students')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {isEdit ? t('modules.students.editTitle') : t('modules.students.createTitle')}
          </h1>
          <p className="text-gray-500">{t('modules.students.editDesc')}</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('modules.students.basicInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="studentNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.studentNumber')}</FormLabel>
                      <FormControl>
                        <Input placeholder="20250001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.email')}</FormLabel>
                      <FormControl>
                        <Input placeholder="student@university.ac.jp" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="nameLast"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.nameLast')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="nameFirst"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.nameFirst')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="nameKanaLast"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.kanaLast')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="nameKanaFirst"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.kanaFirst')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {t('modules.students.affiliation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="structureId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.structure')}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || undefined}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="所属を選択" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {structures.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.nameFormal || s.nameShort}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="enrollmentYear"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.students.enrollmentYear')}</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.students.status')}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="ステータスを選択" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="ENROLLED">{t('modules.students.statuses.ENROLLED')}</SelectItem>
                        <SelectItem value="LEAVE">{t('modules.students.statuses.LEAVE')}</SelectItem>
                        <SelectItem value="GRADUATED">{t('modules.students.statuses.GRADUATED')}</SelectItem>
                        <SelectItem value="WITHDRAWN">{t('modules.students.statuses.WITHDRAWN')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-between items-center">
            <Button variant="outline" type="button" onClick={() => navigate('/admin/enrollment/students')}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" /> {t('common.save')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
